import { useState, useEffect } from "react";
import { Helmet } from "react-helmet";
import { PostCardSkeleton, HeroSkeleton } from "@/components/ui/skeleton";
import { useScrollHeader } from '@/hooks/useScrollHeader';
import { useScrollReveal } from '@/hooks/useScrollReveal';
import { useNavigate, Link } from 'react-router-dom';
import { Search } from 'lucide-react';
import inzLogo from '../assets/inz-logo-optimized.png';
import { PostCard } from '@/components/PostCard';
import { FirstPostCard } from '@/components/FirstPostCard';

const Index = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [recentPosts, setRecentPosts] = useState([]);
  const [recentProjects, setRecentProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [projectsLoading, setProjectsLoading] = useState(true);
  const [isProcessing, setIsProcessing] = useState(false);
  const [processingStep, setProcessingStep] = useState(0);
  const isHeaderVisible = useScrollHeader();
  const navigate = useNavigate();

  // Get greeting based on time
  const getGreeting = () => {
    const hour = new Date().getHours();
    
    if (hour >= 5 && hour < 12) {
      return "Good Morning Buddy! ☕";
    } else if (hour >= 12 && hour < 17) {
      return "Good Afternoon Buddy! 🌞";
    } else if (hour >= 17 && hour < 22) {
      return "Good Evening Buddy! 🌙";
    } else {
      return "Time to Sleep, Good Night! 😴";
    }
  };

  // Initialize scroll reveal effect - re-run when posts/projects load
  useScrollReveal([loading, recentPosts, projectsLoading, recentProjects]);

  const processingSteps = [
    "Initializing search...",
    "Scanning database...",
    "Processing query...",
    "Fetching results...",
    "Almost there..."
  ];

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim() && !isProcessing) {
      setIsProcessing(true);
      setProcessingStep(0);
      
      // Animate through processing steps
      const stepInterval = setInterval(() => {
        setProcessingStep(prev => {
          if (prev < processingSteps.length - 1) {
            return prev + 1;
          }
          return prev;
        });
      }, 800);
      
      // Navigate after 4 seconds
      setTimeout(() => {
        clearInterval(stepInterval);
        navigate(`/posts?search=${encodeURIComponent(searchQuery.trim())}`);
        setSearchQuery('');
        setIsProcessing(false);
        setProcessingStep(0);
        setIsMenuOpen(false);
      }, 4000);
    }
  };

  useEffect(() => {
    // Track homepage visit
    const trackVisit = async () => {
      try {
        const { supabase } = await import('@/integrations/supabase/client');
        await supabase.functions.invoke('increment-homepage-view');
      } catch (error) {
        // Silently fail - tracking is not critical
        if (import.meta.env.DEV) {
          console.log('Homepage tracking:', error);
        }
      }
    };

    trackVisit();

    // Fetch recent posts from database
    const fetchRecentPosts = async () => {
      try {
        setLoading(true);
        const { supabase } = await import('@/integrations/supabase/client');
        const { data, error } = await supabase
          .from('posts')
          .select('id, title, slug, description, thumbnail, category, created_at')
          .eq('published', true)
          .order('created_at', { ascending: false })
          .limit(3);

        if (error) {
          console.error('Error fetching posts:', error);
          throw error;
        }

        const transformedPosts = data?.map(post => ({
          id: post.id,
          title: post.title,
          slug: post.slug,
          description: post.description,
          thumbnail: post.thumbnail,
          category: post.category,
          date: post.created_at,
        })) || [];

        console.log('Fetched posts:', transformedPosts.length);
        setRecentPosts(transformedPosts);
      } catch (error) {
        console.error('Error fetching posts:', error);
        setRecentPosts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchRecentPosts();

    // Fetch recent projects from database (projects are posts with certain categories)
    const fetchRecentProjects = async () => {
      try {
        setProjectsLoading(true);
        const { supabase } = await import('@/integrations/supabase/client');
        const { data, error } = await supabase
          .from('posts')
          .select('id, title, slug, description, thumbnail, category, created_at')
          .eq('published', true)
          .order('created_at', { ascending: false })
          .limit(10); // Get more to filter

        if (error) {
          console.error('Error fetching projects:', error);
          throw error;
        }

        // Filter for project-related categories
        const projectKeywords = ['project', 'app', 'application', 'web', 'mobile', 'tool', 'api'];
        const transformedProjects = data
          ?.filter((post: any) => {
            const categories = typeof post.category === 'string' 
              ? post.category.toLowerCase() 
              : Array.isArray(post.category) 
                ? post.category.map((c: string) => c.toLowerCase()).join(' ')
                : '';
            return projectKeywords.some(keyword => categories.includes(keyword));
          })
          .slice(0, 3)
          .map((project: any) => ({
            id: project.id,
            title: project.title,
            slug: project.slug,
            description: project.description,
            thumbnail: project.thumbnail,
            category: typeof project.category === 'string' 
              ? project.category.split(', ')[0] 
              : Array.isArray(project.category) 
                ? project.category[0]
                : 'Project',
            date: project.created_at,
          })) || [];

        console.log('Fetched projects:', transformedProjects.length);
        setRecentProjects(transformedProjects);
      } catch (error) {
        console.error('Error fetching projects:', error);
        setRecentProjects([]);
      } finally {
        setProjectsLoading(false);
      }
    };

    fetchRecentProjects();
  }, []);

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };


  const schemaOrgJSONLD = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "InvizHer",
    "description": "Your gateway to programming tutorials, innovative projects, and cutting-edge development resources. Learn coding, web development, and technology.",
    "url": "https://invizher.com",
    "potentialAction": {
      "@type": "SearchAction",
      "target": "https://invizher.com/posts?search={search_term_string}",
      "query-input": "required name=search_term_string"
    },
    "publisher": {
      "@type": "Organization",
      "name": "InvizHer",
      "logo": {
        "@type": "ImageObject",
        "url": "https://invizher.com/favicon.ico"
      }
    }
  };

  return (
    <>
      <Helmet>
        <title>InvizHer | Programming Tutorials, Projects & Development Resources</title>
        <meta name="description" content="Programming tutorials, tech projects & development resources. Learn coding concepts, explore projects, and access free materials to enhance your skills." />
        <meta name="keywords" content="programming tutorials, web development, coding projects, JavaScript, Python, React, technology blog, software development, coding guides" />
        <link rel="canonical" href="https://invizher.com/" />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://invizher.com/" />
        <meta property="og:title" content="InvizHer | Programming Tutorials, Projects & Development Resources" />
        <meta property="og:description" content="Programming tutorials, tech projects & development resources. Learn coding concepts, explore projects, and access free materials." />
        <meta property="og:image" content="https://invizher.com/placeholder.svg" />
        <meta property="og:site_name" content="InvizHer" />
        
        {/* Twitter Card */}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:url" content="https://invizher.com/" />
        <meta name="twitter:title" content="InvizHer | Programming Tutorials & Projects" />
        <meta name="twitter:description" content="Programming tutorials, tech projects & development resources. Learn coding concepts and explore projects." />
        <meta name="twitter:image" content="https://invizher.com/placeholder.svg" />
        
        {/* JSON-LD Structured Data */}
        <script type="application/ld+json">
          {JSON.stringify(schemaOrgJSONLD)}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            "name": "InvizHer",
            "url": "https://invizher.com",
            "logo": "https://invizher.com/favicon.ico",
            "description": "Programming tutorials, tech projects, and development resources",
            "sameAs": [
              "https://youtube.com/@invizher",
              "https://instagram.com/invizher",
              "https://t.me/invizher",
              "https://github.com/PavanMagar"
            ],
            "contactPoint": {
              "@type": "ContactPoint",
              "contactType": "customer support",
              "email": "invizher@gmail.com"
            }
          })}
        </script>
      </Helmet>

    <div>

      {/* Enhanced Header */}
      <header className={`fixed w-full z-50 transition-transform duration-300 ${isHeaderVisible ? 'translate-y-0' : '-translate-y-full'}`} id="header" role="banner">
        <nav className="glass mx-4 my-4 rounded-2xl">
          <div className="container mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              {/* Logo */}
              <Link to="/" className="flex items-center space-x-4">
                <div className="relative w-8 h-8">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg"></div>
                  <img 
                    src={inzLogo} 
                    alt="InvizHer Logo" 
                    className="relative w-full h-full rounded-lg object-cover" 
                    width="32"
                    height="32"
                  />
                </div>
                <h1 className="text-xl font-bold gradient-text" title="InvizHer">InvizHer</h1>
              </Link>

              {/* Desktop Navigation */}
              <div className="hidden lg:flex items-center space-x-6" role="menu">
                <Link to="/posts" className="text-gray-700 hover:text-blue-500 transition-colors">Posts</Link>
                <Link to="/projects" className="text-gray-700 hover:text-blue-500 transition-colors">Projects</Link>
                <Link to="/languages" className="text-gray-700 hover:text-blue-500 transition-colors">Languages</Link>
                <Link to="/categories" className="text-gray-700 hover:text-blue-500 transition-colors">Categories</Link>
                
                {/* Enhanced Search Box */}
                <form onSubmit={handleSearch} className="relative">
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search posts..."
                    className="w-56 pl-4 pr-11 py-2.5 rounded-xl border-2 border-gray-200 bg-white/80 backdrop-blur-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none text-sm placeholder:text-gray-400"
                  />
                  <button
                    type="submit"
                    aria-label="Search posts"
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90 transition-all"
                  >
                    <Search className="w-4 h-4" />
                  </button>
                </form>
              </div>

              {/* Mobile Menu Button */}
              <button className="lg:hidden p-2 rounded-lg hover:bg-gray-100 transition-colors" onClick={toggleMenu} aria-label="Toggle mobile menu" aria-expanded={isMenuOpen}>
                <div className="icon-container">
                  <div className={`menu-icon ${isMenuOpen ? 'hidden-icon' : ''}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16m-16 6h16"></path>
                    </svg>
                  </div>
                  <div className={`close-icon ${!isMenuOpen ? 'hidden-icon' : ''}`}>
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12"></path>
                    </svg>
                  </div>
                </div>
              </button>
            </div>
          </div>
        </nav>

        {/* Enhanced Mobile Menu */}
        <div className={`lg:hidden ${isMenuOpen ? 'slide-down' : 'hidden'}`} id="mobile-menu" role="menu">
          <div className="bg-white mx-6 mt-2 p-6 px-4 rounded-xl">
            <nav className="flex flex-col space-y-4">
              {/* Enhanced Search Box for Mobile */}
              <form onSubmit={handleSearch} className="relative mb-2">
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  placeholder="Search posts..."
                  className="w-full pl-4 pr-14 py-3 rounded-xl border-2 border-gray-200 bg-white/80 backdrop-blur-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-500/20 transition-all outline-none placeholder:text-gray-400"
                />
                <button
                  type="submit"
                  className="absolute right-2 top-1/2 -translate-y-1/2 p-2 rounded-lg bg-gradient-to-r from-blue-500 to-purple-500 text-white hover:opacity-90 transition-all"
                >
                  <Search className="w-5 h-5" />
                </button>
              </form>

              <Link to="/posts" className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-all group">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center group-hover:scale-95 transition-transform">
                  <i className="fas fa-newspaper gradient-text text-sm"></i>
                </div>
                <div className="flex-1 pl-2">
                  <span className="font-medium text-gray-700">Posts</span>
                  <p className="text-sm text-gray-500">Articles & tutorials</p>
                </div>
              </Link>

              <Link to="/projects" className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-all group">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center group-hover:scale-95 transition-transform">
                  <i className="fas fa-project-diagram gradient-text text-sm"></i>
                </div>
                <div className="flex-1 pl-2">
                  <span className="font-medium text-gray-700">Projects</span>
                  <p className="text-sm text-gray-500">Explore all projects</p>
                </div>
              </Link>

              <Link to="/languages" className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-all group">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center group-hover:scale-95 transition-transform">
                  <i className="fas fa-code gradient-text text-sm"></i>
                </div>
                <div className="flex-1 pl-2">
                  <span className="font-medium text-gray-700">Languages</span>
                  <p className="text-sm text-gray-500">Programming languages</p>
                </div>
              </Link>

              <Link to="/categories" className="flex items-center space-x-4 p-3 rounded-xl hover:bg-gray-50 transition-all group">
                <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-blue-500/10 to-purple-500/10 flex items-center justify-center group-hover:scale-95 transition-transform">
                  <i className="fas fa-th-large gradient-text text-sm"></i>
                </div>
                <div className="flex-1 pl-2">
                  <span className="font-medium text-gray-700">Categories</span>
                  <p className="text-sm text-gray-500">Browse categories</p>
                </div>
              </Link>

              {/* About and Contact Buttons Row */}
              <div className="grid grid-cols-2 gap-2 mt-2 pt-3 border-t border-gray-100">
                <Link 
                  to="/about" 
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-blue-500 hover:text-blue-500 transition-all text-sm"
                >
                  <i className="fas fa-info-circle text-xs"></i>
                  <span>About</span>
                </Link>
                <Link 
                  to="/contact" 
                  className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg border border-gray-200 text-gray-700 hover:border-blue-500 hover:text-blue-500 transition-all text-sm"
                >
                  <i className="fas fa-envelope text-xs"></i>
                  <span>Contact</span>
                </Link>
              </div>
            </nav>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="pt-28" role="main">
        {/* Modern Hero Section */}
        <section className="relative container mx-auto px-4 py-8 md:py-12 lg:py-16 overflow-hidden">
          <div className="relative glass rounded-2xl p-6">
            {/* Welcome Badge with Time-based Greeting */}
            <div className="flex justify-center mb-6 scroll-reveal">
              <div className="inline-flex items-center space-x-2 px-5 py-2.5 rounded-full glass border border-white/20">
                <div className="flex space-x-1">
                  <div className="w-2 h-2 rounded-full bg-gradient-to-r from-blue-500 to-purple-500 animate-pulse"></div>
                </div>
                <span className="text-sm font-medium gradient-text">{getGreeting()}</span>
              </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid lg:grid-cols-2 gap-8 lg:gap-12 items-center">
              {/* Left Column - Text Content */}
              <div className="space-y-8 text-center lg:text-left">
                {/* Main Heading with Gradient */}
                <div className="space-y-4 scroll-reveal">
                  <h1 className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-bold leading-tight">
                    Programming Tutorials
                    <span className="block mt-2 gradient-text">& Tech Projects</span>
                  </h1>
                  <p className="text-base sm:text-lg lg:text-lg text-gray-600 max-w-xl mx-auto lg:mx-0 leading-relaxed">
                    Learn coding concepts, explore <Link to="/projects" className="text-blue-600 hover:underline">development projects</Link>, and access free <Link to="/posts" className="text-blue-600 hover:underline">programming resources</Link>
                  </p>
                </div>

                {/* Quick Action Buttons - Desktop */}
                <div className="hidden lg:flex items-center gap-4 scroll-reveal">
                  <Link 
                    to="/posts" 
                    className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl hover:shadow-xl hover:shadow-blue-500/50 transition-all flex items-center gap-2 font-medium"
                  >
                    <span>Posts</span>
                    <svg className="w-5 h-5 animate-[slideArrow_1.5s_ease-in-out_infinite]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                    </svg>
                  </Link>
                  <Link 
                    to="/projects" 
                    className="px-8 py-4 glass-morphism border border-gray-300 text-gray-700 rounded-2xl hover:border-blue-500 hover:text-blue-500 transition-all flex items-center gap-2 font-medium"
                  >
                    <i className="fas fa-th-large text-sm"></i>
                    <span>Projects</span>
                  </Link>
                </div>
              </div>

              {/* Right Column - Search Console */}
              <div className="scroll-reveal">
                {/* Console Search Box */}
                <div className="max-w-3xl mx-auto mb-8">
                  <div className="bg-gray-900 text-white rounded-2xl shadow-2xl border border-gray-800 overflow-hidden">
                    {/* Console Header */}
                    <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-gray-800">
                      <div className="flex items-center space-x-2">
                        <div className="w-3 h-3 rounded-full bg-red-400"></div>
                        <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                        <div className="w-3 h-3 rounded-full bg-green-400"></div>
                      </div>
                      <div className="text-xs sm:text-sm text-gray-400 font-mono">search.sh</div>
                    </div>
                    
                    {/* Console Body */}
                    <div className="p-4 sm:p-6">
                      <form onSubmit={handleSearch} className="space-y-3">
                        {/* Console Prompt */}
                        <div className="flex items-start space-x-2 sm:space-x-3">
                          <span className="text-green-400 font-mono text-sm sm:text-base flex-shrink-0 mt-0.5">➜</span>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="text-blue-400 font-mono text-xs sm:text-sm">~</span>
                              <span className="text-purple-400 font-mono text-xs sm:text-sm">search</span>
                            </div>
                            <input
                              type="text"
                              value={searchQuery}
                              onChange={(e) => setSearchQuery(e.target.value)}
                              placeholder="Enter your search query..."
                              className="w-full bg-transparent text-white font-mono text-sm sm:text-base outline-none placeholder:text-gray-500"
                              disabled={isProcessing}
                            />
                          </div>
                        </div>
                        
                        {/* Processing Animation */}
                        {isProcessing && (
                          <div className="space-y-2 pl-6 sm:pl-8 animate-fade-in">
                            <div className="flex items-center space-x-2 text-yellow-400 font-mono text-xs sm:text-sm">
                              <div className="flex space-x-1">
                                <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                                <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                                <div className="w-1.5 h-1.5 bg-yellow-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
                              </div>
                              <span>{processingSteps[processingStep]}</span>
                            </div>
                            <div className="flex items-center space-x-2 text-gray-500 font-mono text-xs">
                              <span>Query:</span>
                              <span className="text-green-400">{searchQuery}</span>
                            </div>
                          </div>
                        )}
                        
                        {/* Helper Text with better contrast */}
                        {!isProcessing && (
                          <div className="flex items-center space-x-2 text-gray-400 font-mono text-xs sm:text-sm pl-6 sm:pl-8">
                            <span className="hidden sm:inline text-gray-300">Press</span>
                            <kbd className="px-2 py-0.5 bg-gray-800 border border-gray-600 rounded text-gray-200 text-xs">Enter</kbd>
                            <span className="text-gray-300">to search</span>
                          </div>
                        )}
                      </form>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Action Buttons - Mobile */}
            <div className="flex lg:hidden flex-row items-center justify-center gap-4 mt-8 scroll-reveal">
             
              <Link 
                to="/projects" 
                className="flex-1 px-6 py-4 glass-morphism border border-gray-300 text-gray-700 rounded-2xl hover:border-blue-500 hover:text-blue-500 transition-all flex items-center justify-center gap-2 font-medium"
              >
                <i className="fas fa-th-large text-sm"></i>
                <span>Projects</span>
              </Link>

               <Link 
                to="/posts" 
                className="group flex-1 px-6 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-2xl hover:shadow-xl hover:shadow-blue-500/50 transition-all flex items-center justify-center gap-2 font-medium"
              >
                <span>Posts</span>
                <svg className="w-5 h-5 animate-[slideArrow_1.5s_ease-in-out_infinite]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                </svg>
              </Link>
            </div>
          </div>
        </section>

        {/* Enhanced Recent Posts Section */}
        <section className="container mx-auto px-6 py-16">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 gradient-text">Latest Programming Tutorials</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Explore our most recent <Link to="/posts" className="text-blue-600 hover:underline">coding tutorials</Link> and development articles</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {loading ? (
              // Show skeleton loaders while loading
              Array(3).fill(0).map((_, i) => (
                <PostCardSkeleton key={i} />
              ))
            ) : recentPosts.length > 0 ? (
              // Show posts when loaded - use FirstPostCard for first post (LCP optimization)
              recentPosts.map((post, index) => index === 0 ? <FirstPostCard key={post.id} post={post} /> : <PostCard key={post.id} post={post} />)
            ) : (
              // Show message when no posts found
              <div className="col-span-full text-center py-12">
                <div className="text-gray-500">
                  <i className="fas fa-newspaper text-4xl mb-4"></i>
                  <p className="text-xl">No posts available yet</p>
                  <p className="text-sm mt-2">Check back soon for new content!</p>
                </div>
              </div>
            )}
          </div>
          
          {!loading && recentPosts.length > 0 && (
            <div className="text-center mt-12 scroll-reveal">
              <Link to="/posts" className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:opacity-90 transition-opacity shine-effect inline-flex items-center">
                <span>View All Posts</span>
                <svg className="w-4 h-4 ml-2 transition-transform duration-300 transform group-hover:translate-x-1 group-hover:-translate-x-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                </svg>
              </Link>
            </div>
          )}
        </section>

        {/* Recent Projects Section */}
        <section className="container mx-auto px-6 py-16">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 gradient-text">Recent Development Projects</h2>
            <p className="text-gray-600 max-w-2xl mx-auto">Check out our latest <Link to="/projects" className="text-blue-600 hover:underline">tech projects</Link> and coding examples</p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
            {projectsLoading ? (
              // Show skeleton loaders while loading
              Array(3).fill(0).map((_, i) => (
                <PostCardSkeleton key={i} />
              ))
            ) : recentProjects.length > 0 ? (
              // Show projects when loaded
              recentProjects.map((project) => (
                <article 
                  key={project.id} 
                  className="scroll-reveal group" 
                  role="article"
                >
                  <Link to={`/posts/${project.slug}`} className="block">
                    <div className="glass-morphism rounded-2xl overflow-hidden hover:shadow-xl transition-all p-6 md:p-8 border border-border/50">
                      {/* Top Section: Thumbnail + Category Badge */}
                      <div className="flex items-start justify-between mb-6">
                        {/* Rectangle Thumbnail */}
                        <div className="w-24 h-16 rounded-2xl overflow-hidden flex-shrink-0 border border-border">
                          <img 
                            src={project.thumbnail} 
                            srcSet={`${project.thumbnail}?width=200&quality=75 200w, ${project.thumbnail}?width=300&quality=75 300w`}
                            sizes="96px"
                            alt={project.title} 
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110" 
                            loading="lazy"
                            width="96"
                            height="64"
                          />
                        </div>
                        
                        {/* Category Badge */}
                        <span className="px-4 py-1 bg-gradient-to-r from-blue-500/10 to-purple-500/10 text-blue-600 rounded-full text-sm font-medium">
                          {project.category}
                        </span>
                      </div>

                      {/* Title */}
                      <h3 className="text-xl md:text-2xl font-bold mb-3 line-clamp-2">
                        {project.title}
                      </h3>
                      
                      {/* Description */}
                      <p className="text-muted-foreground mb-6 line-clamp-2">
                        {project.description}
                      </p>
                    </div>
                  </Link>
                </article>
              ))
            ) : (
              // Show message when no projects found
              <div className="col-span-full text-center py-12">
                <div className="text-gray-500">
                  <i className="fas fa-project-diagram text-4xl mb-4"></i>
                  <p className="text-xl">No projects available yet</p>
                  <p className="text-sm mt-2">Check back soon for new projects!</p>
                </div>
              </div>
            )}
          </div>
          
          {!projectsLoading && recentProjects.length > 0 && (
            <div className="text-center mt-12 scroll-reveal">
              <Link to="/projects" className="group px-8 py-4 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:opacity-90 transition-opacity shine-effect inline-flex items-center">
                <span>View All Projects</span>
                <svg className="w-4 h-4 ml-2 transition-transform duration-300 transform group-hover:translate-x-1 group-hover:-translate-x-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                </svg>
              </Link>
            </div>
          )}
        </section>

        {/* Redesigned Explore Topics Section */}
        <section id="Topics" className="container mx-auto px-6 py-16">
          <div className="text-center mb-12 scroll-reveal">
            <h2 className="text-3xl lg:text-4xl font-bold mb-4 gradient-text">Explore Topics</h2>
            <p className="text-gray-600 mb-12 max-w-2xl mx-auto">Dive into our carefully curated categories and discover a world of knowledge</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {/* Programming Languages Topic */}
            <div className="topic-card glass rounded-2xl p-8 relative overflow-hidden group hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="topic-icon bg-gradient-to-r from-blue-500 to-purple-500 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:-translate-y-2 transition-transform">
                  <i className="fas fa-code text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Languages</h3>
                <p className="text-gray-600 mb-4">Explore multiple programming languages and their ecosystems</p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-check-circle text-blue-500 mr-2"></i>
                    <span>Python, JavaScript, Java</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-check-circle text-blue-500 mr-2"></i>
                    <span>Web Development</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-check-circle text-blue-500 mr-2"></i>
                    <span>Mobile Development</span>
                  </div>
                </div>
                <Link to="/languages" className="flex items-center text-blue-500 group/link">
                  <span className="mr-2 group-hover/link:mr-4 transition-all">Explore Languages</span>
                  <svg className="w-4 h-4 group-hover/link:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                  </svg>
                </Link>
              </div>
            </div>

            {/* Categories Topic */}
            <div className="topic-card glass rounded-2xl p-8 relative overflow-hidden group hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="topic-icon bg-gradient-to-r from-blue-500 to-purple-500 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:-translate-y-2 transition-transform">
                  <i className="fas fa-th-large text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Categories</h3>
                <p className="text-gray-600 mb-4">Browse through various development topics and domains</p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-check-circle text-blue-500 mr-2"></i>
                    <span>Frontend Development</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-check-circle text-blue-500 mr-2"></i>
                    <span>Backend Development</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-check-circle text-blue-500 mr-2"></i>
                    <span>DevOps & Cloud</span>
                  </div>
                </div>
                <Link to="/categories" className="flex items-center text-blue-500 group/link">
                  <span className="mr-2 group-hover/link:mr-4 transition-all">Browse Categories</span>
                  <svg className="w-4 h-4 group-hover/link:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                  </svg>
                </Link>
              </div>
            </div>

            {/* All Posts Topic */}
            <div className="topic-card glass rounded-2xl p-8 relative overflow-hidden group hover:shadow-xl transition-all duration-300 scroll-reveal">
              <div className="absolute top-0 right-0 w-32 h-32 bg-blue-500/10 rounded-full -mr-16 -mt-16 group-hover:scale-150 transition-transform duration-500"></div>
              <div className="relative z-10">
                <div className="topic-icon bg-gradient-to-r from-blue-500 to-purple-500 w-14 h-14 rounded-2xl flex items-center justify-center mb-6 group-hover:-translate-y-2 transition-transform">
                  <i className="fas fa-book text-white text-2xl"></i>
                </div>
                <h3 className="text-xl font-bold mb-3">Resources</h3>
                <p className="text-gray-600 mb-4">Curated collection of development tools and resources</p>
                <div className="space-y-2 mb-6">
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-check-circle text-blue-500 mr-2"></i>
                    <span>Documentation</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-check-circle text-blue-500 mr-2"></i>
                    <span>Tutorials & Guides</span>
                  </div>
                  <div className="flex items-center text-sm text-gray-500">
                    <i className="fas fa-check-circle text-blue-500 mr-2"></i>
                    <span>Best Practices</span>
                  </div>
                </div>
                <a href="resources" className="flex items-center text-blue-500 group/link">
                  <span className="mr-2 group-hover/link:mr-4 transition-all">Access Resources</span>
                  <svg className="w-4 h-4 group-hover/link:translate-x-2 transition-transform" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 8l4 4m0 0l-4 4m4-4H3"></path>
                  </svg>
                </a>
              </div>
            </div>
          </div>
        </section>

        {/* Stay Connected Section */}
        <section className="py-16 md:py-24">
          <div className="container mx-auto px-6 max-w-6xl">
            <div className="grid grid-cols-1 gap-12 items-center">
              <div className="text-center scroll-reveal">
                <h2 className="text-3xl md:text-4xl font-bold mb-4 gradient-text">Stay Connected</h2>
                <p className="text-gray-600 mb-12 max-w-2xl mx-auto">Join our community across social platforms</p>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6 max-w-4xl mx-auto">
                  {/* Telegram */}
                  <a href="https://t.me/invizher" target="_blank" rel="noopener noreferrer" className="group glass p-6 rounded-2xl hover:scale-105 transition-all duration-300 cursor-pointer scroll-reveal">
                    <div className="relative">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center transform group-hover:-translate-y-1 transition-transform duration-300">
                        <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0C5.373 0 0 5.373 0 12s5.373 12 12 12 12-5.373 12-12S18.627 0 12 0zm5.562 8.161c-.18 1.897-.962 6.502-1.359 8.627-.168.9-.5 1.201-.82 1.23-.697.064-1.226-.461-1.901-.903-1.056-.693-1.653-1.124-2.678-1.8-1.185-.781-.417-1.21.258-1.91.177-.184 3.247-2.977 3.307-3.23.007-.032.014-.15-.056-.212s-.174-.041-.249-.024c-.106.024-1.793 1.139-5.062 3.345-.479.329-.913.489-1.302.481-.428-.008-1.252-.241-1.865-.44-.752-.244-1.349-.374-1.297-.789.027-.216.325-.437.893-.663 3.498-1.524 5.83-2.529 6.998-3.014 3.332-1.386 4.025-1.627 4.476-1.635.099-.002.321.023.465.14.121.099.155.232.171.326.016.094.037.308.02.475z"/>
                        </svg>
                      </div>
                      <span className="block text-sm font-medium text-gray-700 group-hover:text-blue-500 transition-colors">Telegram</span>
                      <div className="absolute inset-0 bg-blue-400/10 blur-2xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                  </a>

                  {/* Youtube */}
                  <a href="https://youtube.com/@invizher" target="_blank" rel="noopener noreferrer" className="group glass p-6 rounded-2xl hover:scale-105 transition-all duration-300 cursor-pointer scroll-reveal">
                    <div className="relative">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-red-400 to-red-600 flex items-center justify-center transform group-hover:-translate-y-1 transition-transform duration-300">
                        <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                        </svg>
                      </div>
                      <span className="block text-sm font-medium text-gray-700 group-hover:text-red-500 transition-colors">YouTube</span>
                      <div className="absolute inset-0 bg-red-400/10 blur-2xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                  </a>

                  {/* Instagram */}
                  <a href="https://www.instagram.com/invizher/" target="_blank" rel="noopener noreferrer" className="group glass p-6 rounded-2xl hover:scale-105 transition-all duration-300 cursor-pointer scroll-reveal">
                    <div className="relative">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-pink-400 to-purple-500 flex items-center justify-center transform group-hover:-translate-y-1 transition-transform duration-300">
                        <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                        </svg>
                      </div>
                      <span className="block text-sm font-medium text-gray-700 group-hover:text-purple-500 transition-colors">Instagram</span>
                      <div className="absolute inset-0 bg-purple-400/10 blur-2xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                  </a>

                  {/* Github */}
                  <a href="https://github.com/PavanMagar" target="_blank" rel="noopener noreferrer" className="group glass p-6 rounded-2xl hover:scale-105 transition-all duration-300 cursor-pointer scroll-reveal">
                    <div className="relative">
                      <div className="w-12 h-12 mx-auto mb-4 rounded-xl bg-gradient-to-br from-gray-700 to-gray-600 flex items-center justify-center transform group-hover:-translate-y-1 transition-transform duration-300">
                        <svg className="w-6 h-6 text-white" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z"/>
                        </svg>
                      </div>
                      <span className="block text-sm font-medium text-gray-700 group-hover:text-gray-600 transition-colors">GitHub</span>
                      <div className="absolute inset-0 bg-gray-400/10 blur-2xl -z-10 opacity-0 group-hover:opacity-100 transition-opacity"></div>
                    </div>
                  </a>
                </div>

                <div className="mt-12 scroll-reveal">
                  <p className="text-gray-500 text-sm">Or connect directly via email</p>
                  <Link to="/contact" className="inline-block mt-4 px-8 py-3 bg-gradient-to-r from-blue-500 to-purple-500 text-white rounded-full hover:opacity-90 transition-opacity">
                    Get in Touch
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </section>
      </main>

      {/* Enhanced Footer */}
      <footer className="bg-gray-900 text-white" role="contentinfo">
        <div className="container mx-auto px-4 py-10">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {/* Column 1: About */}
            <div className="space-y-6">
              <div className="flex items-center space-x-4 mb-8">
                <div className="relative w-8 h-8">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-purple-500 rounded-lg"></div>
                  <img src="/inz-logo.png" alt="Logo" className="relative w-full h-full rounded-lg object-cover" />
                </div>
                <span className="text-xl font-bold">InvizHer</span>
              </div>
              <p className="text-gray-400 mb-4">
                Exploring the world of programming and development through providing the thoughtful content.
              </p>
              <div className="flex space-x-4">
                <a href="https://youtube.com/@invizher" className="text-gray-400 hover:text-white transition-colors">
                  <i className="fab fa-youtube"></i>
                </a>
                <a href="https://t.me/invizher" className="text-gray-400 hover:text-white transition-colors">
                  <i className="fab fa-telegram"></i>
                </a>
                <a href="https://www.instagram.com/invizher/" className="text-gray-400 hover:text-white transition-colors">
                  <i className="fab fa-instagram"></i>
                </a>
                <a href="https://github.com/PavanMagar" className="text-gray-400 hover:text-white transition-colors">
                  <i className="fab fa-github"></i>
                </a>
              </div>
            </div>

            {/* Column 2: Quick Links */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Quick Links</h4>
              <ul className="space-y-2">
                <li><Link to="/" className="text-gray-400 hover:text-white transition-colors">Home</Link></li>
                <li><Link to="/posts" className="text-gray-400 hover:text-white transition-colors">Posts</Link></li>
                <li><Link to="/categories" className="text-gray-400 hover:text-white transition-colors">Categories</Link></li>
              </ul>
            </div>

            {/* Column 3: Resources */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Resources</h4>
              <ul className="space-y-2">
                <li><Link to="/faq" className="text-gray-400 hover:text-white transition-colors">FAQ</Link></li>
                <li><Link to="/privacy" className="text-gray-400 hover:text-white transition-colors">Privacy Policy</Link></li>
                <li><Link to="/disclaimer" className="text-gray-400 hover:text-white transition-colors">Disclaimer</Link></li>
              </ul>
            </div>

            {/* Column 4: Support */}
            <div>
              <h4 className="text-lg font-semibold mb-4">Support</h4>
              <ul className="space-y-2">
                <li><Link to="/about" className="text-gray-400 hover:text-white transition-colors">About</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Contact</Link></li>
                <li><Link to="/contact" className="text-gray-400 hover:text-white transition-colors">Report Issue</Link></li>
              </ul>
            </div>
          </div>

          {/* Copyright Section */}
          <div className="border-t border-gray-800 mt-12 pt-8 text-center">
            <p className="text-gray-400">© 2025 InvizHer. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
};

export default Index;
